import React, { useEffect, useMemo } from 'react';
import { CustomerFilterBar, CustomerCard } from './components';
import { CustomerCard as CustomerCardType } from './salesRepCustomersTypes';
import { AsyncBoundary } from '../../../components/AsyncBoundary';
import { useCustomersStore } from '../../../store/customersStore';
import { Customer, mapTierLabel } from '../../../services/customersApi';
import { Tier } from '../shared';
import styles from './SalesRepCustomers.module.css';

function tierToLetter(c: Customer): Tier {
  const label = mapTierLabel(c.pricing_tier_name);
  if (label === 'Gold') return 'A';
  if (label === 'Silver') return 'B';
  return 'C';
}

function relativeTime(iso: string | null): string {
  if (!iso) return 'No orders';
  const ms = Date.now() - new Date(iso).getTime();
  const days = Math.max(0, Math.floor(ms / (1000 * 60 * 60 * 24)));
  if (days === 0) return 'today';
  if (days === 1) return '1d ago';
  return `${days}d ago`;
}

function formatCurrency(n: number): string {
  return `$${Math.round(n).toLocaleString()}`;
}

function adapt(c: Customer): CustomerCardType {
  const tier = tierToLetter(c);
  const segment =
    tier === 'A' ? 'Premium Customer' : tier === 'B' ? 'Standard Customer' : 'Growth Customer';
  // Margin not yet sourced from backend — render a neutral placeholder
  // so the card layout is preserved without fabricating numbers.
  return {
    id: String(c.id),
    name: c.store_name || c.full_name,
    city: [c.city, c.state].filter(Boolean).join(', ') || '—',
    tier,
    segment,
    revenue: formatCurrency(c.metrics.total_revenue),
    lastOrder: relativeTime(c.metrics.last_order_at),
    margin: '—',
    marginColor: c.metrics.outstanding_balance > 0 ? 'red' : 'green',
    profit: formatCurrency(c.metrics.total_revenue - c.metrics.outstanding_balance),
    attentionDot: c.metrics.outstanding_balance > 0,
    phone: c.contact_phone ?? undefined,
  };
}

const SalesRepCustomers: React.FC = () => {
  const items = useCustomersStore((s) => s.items);
  const loading = useCustomersStore((s) => s.loading);
  const error = useCustomersStore((s) => s.error);
  const fetchList = useCustomersStore((s) => s.fetchList);

  useEffect(() => {
    fetchList().catch(() => undefined);
  }, [fetchList]);

  const customers = useMemo<CustomerCardType[]>(() => items.map(adapt), [items]);

  return (
    <div className={styles.container}>
      <CustomerFilterBar />
      <p className={styles.count}>{customers.length} customers found</p>
      <AsyncBoundary
        loading={loading && customers.length === 0}
        error={error}
        empty={!loading && customers.length === 0}
        emptyMessage="No customers assigned to you yet"
        onRetry={() => fetchList()}
      >
        <div className={styles.grid}>
          {customers.map((c) => (
            <CustomerCard key={c.id} customer={c} />
          ))}
        </div>
      </AsyncBoundary>
    </div>
  );
};

export const SalesRepCustomersPage: React.FC = () => {
  return <SalesRepCustomers />;
};

export { SalesRepCustomers };
export default SalesRepCustomers;
